import React, { useEffect, useRef, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button,
  InputAdornment, TextField, Modal, Box, Typography, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import toast from 'react-hot-toast';
import { fetchAdminVoiceSessions, fetchAdminVoiceUsage, resetAdminVoiceUsage, setAdminVoiceAccess } from 'services/adminService';
import { PaginatedSearchRequestDto } from 'models/PaginatedSearchRequestDto';
import { AdminVoiceSessionData, AdminVoiceUsageData, mapVoiceSession, mapVoiceUsage } from 'models/AdminVoiceUsageData';
import { formatLocalDate } from 'Utils/StringUtils';
import { voiceDisplayName } from 'services/voiceService';
import { showAlertDialog } from 'components/Dialog/dialog';

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

const VoiceUsageTable: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const api = useApi();
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<AdminVoiceUsageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 10;
  const totalPages = useRef(1);

  const [selectedUser, setSelectedUser] = useState<AdminVoiceUsageData | null>(null);
  const [sessions, setSessions] = useState<AdminVoiceSessionData[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionHasNext, setSessionHasNext] = useState(false);
  const sessionTotalPages = useRef(1);
  const [accessBusyId, setAccessBusyId] = useState<number | null>(null);
  const [resetBusyId, setResetBusyId] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const load = async () => {
      if (!api) return;
      try {
        setLoading(true);
        setError(null);
        const request = new PaginatedSearchRequestDto({ pageNumber, pageSize, searchTerm });
        const res = await fetchAdminVoiceUsage(api, request);
        const rowsPayload = res?.data ?? res?.Data ?? [];
        const data = Array.isArray(rowsPayload) ? rowsPayload.map(mapVoiceUsage) : [];
        const nextPage = res?.pageNumber ?? res?.PageNumber ?? pageNumber;
        const pages = res?.totalPages ?? res?.TotalPages ?? 0;
        totalPages.current = Math.max(pages, 1);
        if (nextPage > 0 && nextPage !== pageNumber) setPageNumber(nextPage);
        setHasNextPage(nextPage < pages);
        setRows(data);
        setError(data.length < 1 ? 'No voice usage yet.' : null);
      } catch (err: any) {
        console.error(err);
        const message = err?.message || 'Failed to fetch voice usage';
        setError(message);
        toast.error(message);
        setRows([]);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, pageNumber, searchTerm, reloadToken]);

  useEffect(() => {
    const loadSessions = async () => {
      if (!api || !selectedUser) return;
      try {
        setSessionsLoading(true);
        const request = new PaginatedSearchRequestDto({ pageNumber: sessionPage, pageSize: 10 });
        const res = await fetchAdminVoiceSessions(api, selectedUser.userId, request);
        const rows = res?.data ?? res?.Data ?? [];
        const data = Array.isArray(rows) ? rows.map(mapVoiceSession) : [];
        sessionTotalPages.current = res?.totalPages ?? res?.TotalPages ?? 1;
        const currentPage = res?.pageNumber ?? res?.PageNumber ?? 1;
        setSessionHasNext(currentPage < sessionTotalPages.current);
        setSessions(data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch voice sessions');
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    };
    loadSessions();
  }, [api, selectedUser, sessionPage]);

  const openUser = (row: AdminVoiceUsageData) => {
    setSelectedUser(row);
    setSessionPage(1);
  };

  const applyVoiceAccess = (userId: number, enabled: boolean) => {
    setRows((current) => current.map((row) => (
      row.userId === userId
        ? { ...row, voiceAccessEnabled: enabled, activeSessions: enabled ? row.activeSessions : 0 }
        : row
    )));
    setSelectedUser((current) => (
      current && current.userId === userId
        ? { ...current, voiceAccessEnabled: enabled, activeSessions: enabled ? current.activeSessions : 0 }
        : current
    ));
  };

  const resetUsage = async (row: AdminVoiceUsageData) => {
    const confirmed = await showAlertDialog({
      title: 'Reset voice usage',
      description: `Delete all previous voice session logs for ${row.name}? Totals for this user will start from now. This cannot be undone.`,
      agreeText: 'Reset',
      disagreeText: 'Cancel',
    });
    if (!confirmed || !api) return;
    try {
      setResetBusyId(row.userId);
      await resetAdminVoiceUsage(api, row.userId);
      if (selectedUser?.userId === row.userId) {
        setSelectedUser(null);
        setSessions([]);
      }
      setReloadToken((n) => n + 1);
      toast.success(`Voice usage for ${row.name} cleared. New sessions will count from now.`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset voice usage');
    } finally {
      setResetBusyId(null);
    }
  };

  const toggleVoiceAccess = async (row: AdminVoiceUsageData) => {
    const enable = !row.voiceAccessEnabled;
    const confirmed = await showAlertDialog({
      title: enable ? 'Allow voice assistant' : 'Ban from voice assistant',
      description: enable
        ? `Allow ${row.name} to use the voice assistant again?`
        : `Ban ${row.name} from the voice assistant? Any live session will be interrupted immediately.`,
      agreeText: enable ? 'Allow' : 'Ban',
      disagreeText: 'Cancel',
    });
    if (!confirmed || !api) return;
    try {
      setAccessBusyId(row.userId);
      await setAdminVoiceAccess(api, row.userId, enable);
      applyVoiceAccess(row.userId, enable);
      toast.success(enable ? 'Voice access allowed' : 'Voice access banned. Live session interrupted.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update voice access');
    } finally {
      setAccessBusyId(null);
    }
  };

  const headerSx = {
    '& th': {
      backgroundColor: 'var(--accent-color)',
      color: '#ffffff',
      fontWeight: 700,
      padding: '12px 16px',
      fontSize: '0.95rem',
      letterSpacing: '0.01em',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  };

  const cellColor = isDarkMode ? '#ffffff' : '#076596';
  const pagerButtonSx = {
    borderColor: '#076596',
    color: cellColor,
    backgroundColor: isDarkMode ? '#444444' : '#ffffff',
    padding: '8px 16px',
    fontWeight: 500,
    fontSize: '0.9rem',
    '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
    '&.Mui-disabled': {
      borderColor: isDarkMode ? '#555555' : '#cccccc',
      color: isDarkMode ? '#777777' : '#999999',
    },
  };

  const renderPager = (
    page: number,
    total: number,
    hasNext: boolean,
    onPrev: () => void,
    onNext: () => void,
    onSelect: (page: number) => void,
  ) => {
    const pagesToShow: (number | 'ellipsis')[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pagesToShow.push(i);
    } else if (page === 1) {
      pagesToShow.push(1, 2, 'ellipsis', total - 1, total);
    } else if (page < total - 1) {
      pagesToShow.push(page - 1, page, 'ellipsis', total - 1, total);
    } else if (page === total - 1) {
      pagesToShow.push(1, 2, 'ellipsis', page, total);
    } else {
      pagesToShow.push(1, 2, 'ellipsis', page - 1, page);
    }

    return (
      <div className="admin-pagination" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button variant="outlined" disabled={page === 1} onClick={onPrev} sx={pagerButtonSx}>
            Prev
          </Button>
          {pagesToShow.map((p, idx) => {
            if (p === 'ellipsis') {
              return (
                <div key={`ellipsis-${idx}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}>
                  {' '}...{' '}
                </div>
              );
            }
            return (
              <Button
                key={`page-${p}`}
                disabled={p === page}
                variant="outlined"
                onClick={() => onSelect(p)}
                sx={pagerButtonSx}
              >
                {p}
              </Button>
            );
          })}
          <Button variant="outlined" disabled={!hasNext} onClick={onNext} sx={pagerButtonSx}>
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 0, backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>
      <div className="admin-toolbar">
        <TextField
          className="admin-search"
          placeholder="Search voice usage..."
          value={searchTerm}
          onChange={(e) => {
            setPageNumber(1);
            setSearchTerm(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDarkMode ? '#e0e0e0' : '#666666', opacity: 0.8 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: '1 1 200px',
            minWidth: 0,
            '& .MuiInputBase-root': {
              borderRadius: '4px',
              background: isDarkMode ? '#444444' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#076596',
              fontSize: '0.9rem',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              padding: '2px 10px',
            },
            '& .MuiInputBase-input': { padding: '8px 8px 8px 0' },
          }}
          aria-label="search voice usage by name, username, or email"
        />
      </div>
      <TableContainer
        className="admin-table-scroll"
        component={Paper}
        sx={{
          backgroundColor: isDarkMode ? '#444444' : '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid #076596' : '1px solid rgba(255, 255, 255, 0.3)',
          maxHeight: { xs: '60vh', md: '70vh' },
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="voice usage table" stickyHeader sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow sx={headerSx}>
              <TableCell>User ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Sessions</TableCell>
              <TableCell>Total time</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Access</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography sx={{ color: isDarkMode ? '#ffffff' : '#076596', fontSize: '1rem', fontWeight: 500, padding: '16px' }}>
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error && rows.length < 1 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography sx={{ color: isDarkMode ? '#ff6b6b' : '#e74c3c', fontSize: '1rem', fontWeight: 500, padding: '16px' }}>
                    {error}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow
                key={row.userId}
                hover
                onClick={() => openUser(row)}
                sx={{
                  backgroundColor: isDarkMode ? '#444444' : '#ffffff',
                  '& .MuiTableCell-root': { backgroundColor: 'transparent !important' },
                  '&:hover': { backgroundColor: isDarkMode ? '#5a5a5a' : '#becfe6' },
                  '& td, & th': {
                    color: isDarkMode ? '#ffffff' : '#076596',
                    padding: '10px 16px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    borderBottom: isDarkMode
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                  },
                  cursor: 'pointer',
                }}
              >
                <TableCell component="th" scope="row">{row.userId}</TableCell>
                <TableCell>{row.username}</TableCell>
                <TableCell>{row.email || '—'}</TableCell>
                <TableCell>{row.groupName || '—'}</TableCell>
                <TableCell>{row.sessionCount}</TableCell>
                <TableCell>{formatDuration(row.totalDurationSeconds)}</TableCell>
                <TableCell>{row.lastUsedAt ? formatLocalDate(row.lastUsedAt) : '—'}</TableCell>
                <TableCell>
                  {!row.voiceAccessEnabled ? 'Banned' : row.activeSessions > 0 ? 'Active' : 'Idle'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={row.voiceAccessEnabled ? 'error' : 'success'}
                      disabled={accessBusyId === row.userId}
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggleVoiceAccess(row);
                      }}
                      sx={{ minWidth: 72, fontWeight: 600 }}
                    >
                      {row.voiceAccessEnabled ? 'Ban' : 'Allow'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={resetBusyId === row.userId}
                      onClick={(e) => {
                        e.stopPropagation();
                        void resetUsage(row);
                      }}
                      sx={{ minWidth: 72, fontWeight: 600 }}
                    >
                      Reset
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {renderPager(
        pageNumber,
        totalPages.current,
        hasNextPage,
        () => { if (pageNumber > 1) setPageNumber(pageNumber - 1); },
        () => setPageNumber(pageNumber + 1),
        setPageNumber,
      )}

      <Modal open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', sm: 780 },
            bgcolor: isDarkMode ? '#2a2a2a' : '#ffffff',
            boxShadow: 24,
            p: { xs: 2, sm: 3 },
            borderRadius: '12px',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 2,
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ color: cellColor, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {selectedUser?.name} · {formatDuration(selectedUser?.totalDurationSeconds || 0)} in {selectedUser?.sessionCount || 0} sessions
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {selectedUser && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color={selectedUser.voiceAccessEnabled ? 'error' : 'success'}
                    disabled={accessBusyId === selectedUser.userId}
                    onClick={() => void toggleVoiceAccess(selectedUser)}
                    sx={{ fontWeight: 600, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                  >
                    {selectedUser.voiceAccessEnabled ? 'Ban' : 'Allow'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={resetBusyId === selectedUser.userId}
                    onClick={() => void resetUsage(selectedUser)}
                    sx={{ fontWeight: 600, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                  >
                    Reset
                  </Button>
                </>
              )}
              <IconButton onClick={() => setSelectedUser(null)}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <TableContainer className="admin-table-scroll" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow sx={headerSx}>
                <TableCell>Started</TableCell>
                <TableCell>Ended</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Voice</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessionsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: cellColor, py: 2 }}>Loading...</TableCell>
                </TableRow>
              ) : sessions.length < 1 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: cellColor, py: 2 }}>No sessions</TableCell>
                </TableRow>
              ) : sessions.map((session) => (
                <TableRow
                  key={session.id}
                  sx={{
                    backgroundColor: isDarkMode ? '#444444' : '#ffffff',
                    '& .MuiTableCell-root': { backgroundColor: 'transparent !important' },
                    '&:hover': { backgroundColor: isDarkMode ? '#5a5a5a' : '#becfe6' },
                    '& td': {
                      color: isDarkMode ? '#ffffff' : '#076596',
                      padding: '10px 16px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      borderBottom: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <TableCell>{formatLocalDate(session.startedAt)}</TableCell>
                  <TableCell>{session.endedAt ? formatLocalDate(session.endedAt) : '—'}</TableCell>
                  <TableCell>{formatDuration(session.durationSeconds)}</TableCell>
                  <TableCell>{voiceDisplayName(session.voiceId || undefined)}</TableCell>
                  <TableCell>{session.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          {renderPager(
            sessionPage,
            sessionTotalPages.current,
            sessionHasNext,
            () => { if (sessionPage > 1) setSessionPage(sessionPage - 1); },
            () => setSessionPage(sessionPage + 1),
            setSessionPage,
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default VoiceUsageTable;
